// src/redux/slices/appointmentDrawerSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import dayjs, { Dayjs } from "dayjs";
import { IAppointmentType, schedulingV2 } from "lobbie";
import { IFormHelper } from "src/components/pages/staff/patients/dep/AccountPatientForms";

export interface AppointmentDrawerState {
    show: boolean;
    onAppointmentSchedule:
        | ((createDetails: schedulingV2.ISchedulerScheduleUpdateActualizeResponse) => void)
        | null;
    onAppointmentUpdate:
        | ((
              newAppointment: schedulingV2.ISchedulerScheduleUpdateActualizeResponse,
              originalAppointment?: schedulingV2.ISchedulerScheduleUpdateActualizeResponse,
          ) => void)
        | null;
    appointmentId?: number;
    ghostAppointmentSegment?: schedulingV2.IAppointmentSeriesSegmentInfo;
    appointmentType: IAppointmentType | undefined;
    selectedAppointmentTime: Dayjs | undefined;
    buildingContext?: schedulingV2.IAppointmentBuildingContext;
}

export const initialStateAppointmentDrawer: AppointmentDrawerState = {
    show: false,
    onAppointmentSchedule: null,
    onAppointmentUpdate: null,
    appointmentType: undefined,
    selectedAppointmentTime: undefined,
};

const appointmentDrawerSlice = createSlice({
    name: "appointmentDrawer",
    initialState: initialStateAppointmentDrawer,
    reducers: {
        showAppointmentDrawer: (
            state,
            action: PayloadAction<Omit<AppointmentDrawerState, "show">>,
        ) => {
            return {
                ...action.payload,
                show: true,
                // Only set onAppointmentSchedule and onAppointmentUpdate if they are not null
                onAppointmentSchedule:
                    action.payload.onAppointmentSchedule ?? state.onAppointmentSchedule,
                onAppointmentUpdate:
                    action.payload.onAppointmentUpdate ?? state.onAppointmentUpdate,
            };
        },
        setAppointmentDrawerCallbacks: (
            state,
            action: PayloadAction<{
                onAppointmentSchedule: (
                    createDetails: schedulingV2.ISchedulerScheduleUpdateActualizeResponse,
                ) => void;
                onAppointmentUpdate: (
                    appointment: schedulingV2.ISchedulerScheduleUpdateActualizeResponse,
                ) => void;
            }>,
        ) => {
            state.onAppointmentSchedule = action.payload.onAppointmentSchedule;
            state.onAppointmentUpdate = action.payload.onAppointmentUpdate;
        },
        showGhostAppointmentSegment: (
            state,
            action: PayloadAction<{
                ghostDetails: schedulingV2.IAppointmentSeriesSegmentInfo;
                onAppointmentSchedule: (
                    createDetails: schedulingV2.ISchedulerScheduleUpdateActualizeResponse,
                ) => void;
                onAppointmentUpdate: (
                    appointment: schedulingV2.ISchedulerScheduleUpdateActualizeResponse,
                ) => void;
            }>,
        ) => {
            state.show = true;
            state.ghostAppointmentSegment = action.payload.ghostDetails;
            state.appointmentId = undefined;
            state.onAppointmentSchedule = action.payload.onAppointmentSchedule;
            state.onAppointmentUpdate = action.payload.onAppointmentUpdate;
        },
        hideAppointmentDrawer: () => initialStateAppointmentDrawer,
        setAppointmentType: (state, action: PayloadAction<IAppointmentType>) => {
            state.appointmentType = action.payload;
        },
        setAppointmentSelectedTime: (state, action: PayloadAction<Dayjs>) => {
            state.selectedAppointmentTime = action.payload;
        },
        loadExistingAppt: (state, action: PayloadAction<schedulingV2.IAppointmentDrawer>) => {
            if (action.payload.appointmentId) {
                state.appointmentId = action.payload.appointmentId;
            }
            state.appointmentType = action.payload.appointmentType;
            state.selectedAppointmentTime = dayjs(action.payload.startUnix);
        },
        loadGhostAppt: (
            state,
            action: PayloadAction<{
                appointmentType: IAppointmentType;
                selectedTime: Dayjs;
            }>,
        ) => {
            state.appointmentType = action.payload.appointmentType;
            state.selectedAppointmentTime = action.payload.selectedTime;
        },
        // The only case where we need to update the buildingContext outside of show because accountPatient can change
        setSelectedPatientForms: (
            state: AppointmentDrawerState,
            action: PayloadAction<IFormHelper[] | undefined>,
        ): void => {
            if (state.buildingContext) {
                state.buildingContext.selectedPatientForms = action.payload;
            }
        },
        setSchedulerConfigId: (
            state: AppointmentDrawerState,
            action: PayloadAction<number>,
        ): void => {
            if (state.buildingContext) {
                state.buildingContext.schedulerConfigId = action.payload;
            }
        },
    },
});

export const {
    showAppointmentDrawer,
    setAppointmentDrawerCallbacks,
    showGhostAppointmentSegment,
    hideAppointmentDrawer,
    setAppointmentType,
    setAppointmentSelectedTime,
    loadExistingAppt,
    loadGhostAppt,
    setSelectedPatientForms,
    setSchedulerConfigId,
} = appointmentDrawerSlice.actions;
export default appointmentDrawerSlice.reducer;
